import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notify } from "@/lib/notify";
import { ArrowLeft, CalendarPlus, Check, ChevronsUpDown, Save, Trash2 } from "lucide-react";

import { api } from "@/integrations/aws/resources";
import { useConfirm, useConfirmDelete } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/app-shell";
import { TablePagination, usePagination } from "@/components/list-controls";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { todayISO } from "@/lib/fee-schedules";
import { VersionDialog } from "@/components/fee-schedules/version-dialog";
import { StatusBadge } from "@/components/fee-schedules/status-badge";
import { invalidateTargets } from "@/components/fee-schedules/attachment";
import {
  feeScheduleQuery,
  feeScheduleVersionsQuery,
  invalidateStandardList,
  serviceCategoriesQuery,
} from "@/components/fee-schedules/queries";
import type { FeeSchedule, FeeVersion } from "@/components/fee-schedules/types";
import { DataExportMenu } from "@/components/export-menu";
import { exportPDFReport, exportSheetCSV, exportWorkbook, type Row } from "@/lib/export-utils";
import { feeScheduleSheets, fileStem, type PriceListService } from "@/lib/fee-schedule-export";
import { pdfColumns, sheetNamed, sheetRows } from "@/lib/service-export";
import { FREQ_LABEL, type BillingFrequency } from "./services";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Truncated } from "@/components/truncated";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/fee-schedules/$scheduleId")({
  head: () => ({
    meta: [
      { title: "Fee Schedule — Somasi Ops" },
      {
        name: "description",
        content: "The dated price lists a fee schedule has held, and which one bills today.",
      },
    ],
  }),
  component: FeeScheduleDetailPage,
  errorComponent: () => <div className="text-destructive">Could not load this fee schedule.</div>,
  notFoundComponent: () => <div className="text-muted-foreground">Fee schedule not found.</div>,
});

/**
 * Loads the schedule this URL names.
 *
 * `GET /fee-schedules/{id}` rather than finding the row in a list: the page is
 * reachable by its own URL, so it cannot assume the list has been loaded, and a 404
 * from the id in the address bar is the honest answer to a deleted schedule.
 */
function FeeScheduleDetailPage() {
  const { scheduleId } = Route.useParams();
  const { canEdit } = useAuth();
  const qc = useQueryClient();

  const { data: schedule, isLoading } = useQuery(feeScheduleQuery(scheduleId));

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!schedule) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Fee schedule not found</AlertTitle>
        <AlertDescription>
          It may have been deleted. Go back to the{" "}
          <Link to="/fee-schedules" className="underline">
            list of schedules
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <FeeScheduleDetail
      schedule={schedule}
      canEdit={canEdit}
      onChanged={() => {
        qc.invalidateQueries({ queryKey: ["fee_schedule", scheduleId] });
        qc.invalidateQueries({ queryKey: ["fee_schedules"] });
        qc.invalidateQueries({ queryKey: ["fee_schedules_count"] });
      }}
    />
  );
}

/**
 * What a version is doing now, which is not the same as when it starts.
 *
 * "Billing now" is the version whose window contains today — `effective_from` and
 * the derived `effective_to` both inclusive — and not the newest one. The two part
 * company the moment a price change is queued: the newest version has not started,
 * so the one still billing would read as "Superseded" and nothing on the page would
 * read as live. `billingFrom` is that window's start, worked out once by the caller
 * so this and the "Billing today" fact cannot disagree.
 */
function versionState(
  version: FeeVersion,
  billingFrom: string | null,
): { label: string; tone: "live" | "future" | "past" } {
  if (version.effective_from === billingFrom) return { label: "Billing now", tone: "live" };
  if (!version.is_historical) return { label: "Scheduled", tone: "future" };
  return { label: "Superseded", tone: "past" };
}

/**
 * A schedule's price history, as a list of dated versions.
 *
 * This page used to be the price grid itself, which could only ever show one set of
 * prices — so repricing a schedule overwrote what past invoices had been raised at.
 * The grid moved one level down, to a version, and what is left here is the question
 * an operator actually opens a schedule to answer: which prices are billing today,
 * what they were before, and what is queued to take over.
 *
 * Who the schedule prices is deliberately not shown. A company or group points at a
 * schedule from its own record, so a list here could only ever be a second copy of
 * that — which is exactly what the schedule's old scope column was, and what made
 * the two able to disagree.
 */
function FeeScheduleDetail({
  schedule,
  canEdit,
  onChanged,
}: {
  schedule: FeeSchedule;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const confirmDelete = useConfirmDelete();
  const [addingVersion, setAddingVersion] = useState(false);

  // The name and the notes are edited in place at the top of the page, but saved from
  // the header beside the other actions — so the state they share lives here rather
  // than inside the panel that draws them.
  const [name, setName] = useState(schedule.name);
  const [notes, setNotes] = useState(schedule.notes ?? "");
  const [savingDetails, setSavingDetails] = useState(false);

  const textDirty = name !== schedule.name || notes !== (schedule.notes ?? "");

  // A refetch replaces what is on screen only while it is untouched; it must never
  // overwrite a word being typed. Guarded on these two fields alone — an unsaved
  // change to the groups is no reason to stop the name catching up.
  useEffect(() => {
    if (textDirty) return;
    setName(schedule.name);
    setNotes(schedule.notes ?? "");
  }, [schedule.name, schedule.notes, textDirty]);

  // The groups reading this schedule. Edited here and saved with the name and the
  // note, because all three are "what this schedule is" rather than what it charges.
  const { data: allGroups = [] } = useQuery({
    queryKey: ["client_groups_lite"],
    queryFn: async () =>
      ((await api.clientGroups.list().all().select("id, name, fee_schedule_id").order("name"))
        .data ?? []) as { id: string; name: string; fee_schedule_id: string | null }[],
  });

  const attachedIds = useMemo(
    () => (schedule.client_groups ?? []).map((group) => group.id).sort(),
    [schedule.client_groups],
  );
  const [groupIds, setGroupIds] = useState<string[]>(attachedIds);
  const groupsDirty = [...groupIds].sort().join(",") !== attachedIds.join(",");

  useEffect(() => {
    if (groupsDirty) return;
    setGroupIds(attachedIds);
  }, [attachedIds, groupsDirty]);

  const detailsDirty = name !== schedule.name || notes !== (schedule.notes ?? "") || groupsDirty;

  const resetDetails = () => {
    setGroupIds(attachedIds);
    setName(schedule.name);
    setNotes(schedule.notes ?? "");
  };

  const saveDetails = async () => {
    const trimmed = name.trim();
    if (!trimmed) return notify.error("A schedule needs a name");

    setSavingDetails(true);
    try {
      if (textDirty) {
        const { error } = await api.feeSchedules.update(schedule.id, {
          name: trimmed,
          notes: notes.trim() || null,
        });
        if (error) throw new Error(error.message);
      }
      // Attaching moves each group's own pointer, so it is its own request — and a
      // group taken off this schedule reads the level below until it is given another.
      if (groupsDirty) {
        const { error } = await api.feeSchedules.setClientGroups(schedule.id, groupIds);
        if (error) throw new Error(error.message);
        invalidateTargets(qc);
        qc.invalidateQueries({ queryKey: ["client_groups_lite"] });
      }
      setName(trimmed);
      notify.success("Schedule updated");
      onChanged();
      qc.invalidateQueries({ queryKey: ["fee_schedule", schedule.id] });
    } catch (err) {
      notify.error(err);
    } finally {
      setSavingDetails(false);
    }
  };

  const { data: versions = [], isLoading } = useQuery(feeScheduleVersionsQuery(schedule.id));
  const pager = usePagination({ pageSize: 10 });

  const shown = useMemo(
    () => versions.slice(pager.offset, pager.offset + pager.pageSize),
    [versions, pager.offset, pager.pageSize],
  );

  const latest = versions.find((v) => v.is_latest) ?? null;
  const today = todayISO();
  // The version an invoice raised today would bill at: the latest one that has
  // actually started, which is not the newest when a future version exists.
  const billingNow = useMemo(
    () => versions.find((v) => v.effective_from <= today) ?? versions[versions.length - 1] ?? null,
    [versions, today],
  );

  const openVersion = (effectiveFrom: string) =>
    navigate({
      to: "/fee-schedules/$scheduleId/versions/$effectiveFrom",
      params: { scheduleId: schedule.id, effectiveFrom },
    });

  // Only for naming a service's category in an export. Cheap, cached, and shared
  // with the version screen's filter.
  const { data: categories = [] } = useQuery(serviceCategoriesQuery());
  const categoryName = (id: string | null) =>
    categories.find((category) => category.id === id)?.name ?? "—";

  /**
   * This schedule's price list, assembled when an export is clicked.
   *
   * Two reads, and both have to be reads. The services are the whole catalogue —
   * the table on this page lists dated versions, not services, so there is nothing
   * on screen to export. The prices are the price book scoped to this schedule,
   * which is one request for every version and every price: built from the version
   * routes instead it is one request per dated set, and a version saved while those
   * are in flight lands in some responses and not the rest, so the file would be a
   * mixture of two price lists.
   *
   * The catalogue is read whole, inactive services included, because a service may
   * be marked inactive after this schedule priced it — and that row is exactly the one
   * worth seeing. Every sheet carries `Service active` so a reader can drop them;
   * the PDF, which is the one that gets sent out, drops them itself.
   *
   * `versions: "all"` asks for the whole dated history rather than the set in
   * force. A separate menu item, because it is bigger by however many times the
   * schedule has been repriced.
   */
  const exportSheets = async (scope: "in_force" | "all") => {
    const [services, book] = await Promise.all([
      api.services
        .list()
        .all()
        .select(
          "id, code, name, category_id, default_fee, billing_frequency, invoice_description, active",
        )
        .order("code")
        .then(({ data, error }) => {
          if (error) throw new Error(error.message);
          return (data ?? []) as PriceListService[];
        }),
      api.feeSchedules
        .priceBook({ versions: scope, feeScheduleId: schedule.id })
        .then(({ data, error }) => {
          if (error) throw error;
          /*
           * Found by id, never by position.
           *
           * `fee_schedule_id` is a filter the server may not apply — an API that
           * predates it ignores the unknown parameter and answers with the whole
           * book, ordered by name. Reading `schedules[0]` there is not a near miss:
           * it is the alphabetically first schedule, exported under *this*
           * schedule's name and filename, so every schedule in the tenant
           * downloads as whichever one sorts first and the file looks right.
           *
           * Matching the id costs nothing when the filter did work — the book holds
           * one schedule and this finds it — and is correct when it did not.
           */
          const priced = data?.schedules.find(
            (candidate) => String(candidate.fee_schedule_id) === String(schedule.id),
          );
          // Absent means the schedule was deleted under us. Say so rather than
          // writing an empty file, which is the one outcome indistinguishable from
          // a download that broke.
          if (!data || !priced) throw new Error("This schedule's prices could not be read");
          return { asOf: data.as_of, priced };
        }),
    ]);

    return {
      asOf: book.asOf,
      sheets: feeScheduleSheets(services, book.priced, book.asOf, {
        category: categoryName,
        frequency: (value) => (value ? (FREQ_LABEL[value as BillingFrequency] ?? value) : ""),
      }),
    };
  };

  /** What the schedule is, for the PDF's header. */
  const scheduleSummary = (asOf: string) =>
    [
      `rates in force on ${asOf}`,
      schedule.is_standard ? "the standard price list" : null,
      schedule.active ? null : "schedule inactive",
      (schedule.client_groups ?? []).length
        ? `read by ${(schedule.client_groups ?? []).map((group) => group.name).join(", ")}`
        : "no client group reads it",
    ]
      .filter(Boolean)
      .join(" · ");

  const exportGroups = [
    {
      label: "Excel workbook — every sheet",
      items: [
        {
          label: "Price list with current rates",
          hint: "Every service and the fee this schedule bills for it, plus the dated versions it holds.",
          run: async () => {
            const { sheets } = await exportSheets("in_force");
            exportWorkbook(fileStem(schedule.name), sheets);
          },
        },
        {
          label: "Price list with full history",
          hint: "The same, with every dated version's prices rather than the set in force.",
          run: async () => {
            const { sheets } = await exportSheets("all");
            exportWorkbook(`${fileStem(schedule.name)}-history`, sheets);
          },
        },
      ],
    },
    {
      label: "Single table",
      items: [
        {
          label: "Price list (CSV)",
          hint: "One row per service: the fee, and whether this schedule set it or it is the standard fee.",
          run: async () => {
            const { sheets } = await exportSheets("in_force");
            exportSheetCSV(fileStem(schedule.name), sheetNamed(sheets, "Price list"));
          },
        },
        {
          label: "Negotiated rates only (CSV)",
          hint: "Just the services this schedule prices itself, without the standard-fee fallbacks.",
          run: async () => {
            const { sheets } = await exportSheets("in_force");
            exportSheetCSV(
              `${fileStem(schedule.name)}-negotiated`,
              sheetNamed(sheets, "Negotiated rates"),
            );
          },
        },
        {
          label: "Price history (CSV)",
          hint: "One row per dated version and service — what this schedule charged over each period.",
          run: async () => {
            const { sheets } = await exportSheets("all");
            exportSheetCSV(
              `${fileStem(schedule.name)}-history`,
              sheetNamed(sheets, "Price history"),
            );
          },
        },
        {
          label: "Fee schedule (PDF)",
          hint: "The client-facing price list: what was agreed, then everything else at the standard fee.",
          run: async () => {
            const { asOf, sheets } = await exportSheets("in_force");

            // The document that gets sent out, so inactive services come off it —
            // they are not on sale, and a price beside one invites an order for it.
            const live = (rows: Row[]) => rows.filter((row) => row["Service active"] === "Yes");
            const negotiated = live(sheetRows(sheets, "Negotiated rates"));
            const fallback = live(sheetRows(sheets, "Price list")).filter(
              (row) => row["Fee source"] === "Standard fee",
            );

            const columns = pdfColumns([
              "Service code",
              "Service name",
              "Category",
              "Billing frequency",
              "Fee",
            ]);

            exportPDFReport({
              filename: fileStem(schedule.name),
              title: `Fee Schedule — ${schedule.name}`,
              subtitle: scheduleSummary(asOf),
              filters: `${negotiated.length} service${
                negotiated.length === 1 ? "" : "s"
              } priced on this schedule, ${fallback.length} at the standard fee`,
              sections: [
                {
                  heading: "Priced on this schedule",
                  note: "The rates agreed for this schedule, in force on the date above.",
                  columns,
                  rows: negotiated,
                },
                {
                  heading: "At the standard fee",
                  note: "This schedule sets no rate for these, so the Somasi standard fee applies.",
                  pageBreak: true,
                  columns,
                  rows: fallback,
                },
                {
                  heading: "Dated versions",
                  note: "Each set of prices applies from its own date until the next supersedes it.",
                  pageBreak: true,
                  columns: pdfColumns([
                    "Effective from",
                    "Effective to",
                    "State",
                    "Services priced",
                    "Lowest fee",
                    "Highest fee",
                    "Notes",
                  ]),
                  rows: sheetRows(sheets, "Dated versions"),
                },
              ],
            });
          },
        },
      ],
    },
  ];

  const removeVersion = async (version: FeeVersion) => {
    const only = versions.length === 1;
    const agreed = only
      ? await confirmDelete(
          `the only dated prices on “${schedule.name}”`,
          "The schedule will price nothing, and every service will fall back to the level below it.",
        )
      : await confirmDelete(
          `the prices effective ${version.effective_from}`,
          "Every service price in that set goes with it. Later and earlier sets are untouched.",
        );
    if (!agreed) return;

    const { error } = await api.feeSchedules.removeVersion(schedule.id, version.effective_from);
    if (error) return notify.error(error);
    notify.success(`Prices effective ${version.effective_from} deleted`);
    qc.invalidateQueries({ queryKey: ["fee_schedule_versions", schedule.id] });
    invalidateStandardList(qc);
  };

  /**
   * Switching the schedule on or off.
   *
   * A label, and no longer a kill switch. The resolver decides what a schedule
   * prices from its dated windows alone and does not read `active`, so switching one
   * off marks it as out of use without changing a single fee. What actually stops a
   * company being billed these rates is detaching the schedule on the company or its
   * group — which is what the dialog below says, because a switch that reads as
   * "stop pricing" and silently does nothing is worse than no switch.
   */
  /**
   * Making this the standard price list — the one the service catalogue reads.
   *
   * One schedule carries the flag, and the server moves it rather than copying it,
   * so this both claims it here and clears it wherever it was. That is worth saying
   * out loud before it happens: the services page will start showing *these* rates
   * as every service's standard fee, and whichever list it was reading will stop
   * being the catalogue without anyone visiting its page.
   *
   * Turning it off leaves nothing marked, which is a real state — each service then
   * shows its own stored fee again.
   */
  const toggleStandard = async (isStandard: boolean) => {
    const agreed = await confirm(
      isStandard
        ? {
            title: `Make “${schedule.name}” the standard price list?`,
            description:
              "The services page will show this schedule's rates — the dated set in force — as each service's standard fee, and whichever schedule holds that now will stop being the standard list. Only one can.",
            confirmLabel: "Make it standard",
            tone: "warning",
          }
        : {
            title: `Stop “${schedule.name}” being the standard price list?`,
            description:
              "No schedule will be the standard list, so every service will show its own stored fee until another is marked. The schedule itself and its prices are unchanged.",
            confirmLabel: "Unmark",
            tone: "warning",
          },
    );
    if (!agreed) return;

    const { error } = await api.feeSchedules.update(schedule.id, { is_standard: isStandard });
    if (error) return notify.error(error);

    notify.success(isStandard ? "Standard price list set" : "Standard price list unset");
    onChanged();
    qc.invalidateQueries({ queryKey: ["fee_schedule", schedule.id] });
    // Every schedule's row can change: the flag moved off one of them.
    qc.invalidateQueries({ queryKey: ["fee_schedules"] });
    invalidateStandardList(qc);
  };

  const toggleActive = async (active: boolean) => {
    if (
      !active &&
      !(await confirm({
        title: `Mark “${schedule.name}” inactive?`,
        description:
          "This marks the schedule as out of use. It does not change any fee: companies and groups pointing at it go on being billed these rates. To stop that, detach the schedule on the company or its client group.",
        confirmLabel: "Mark inactive",
        tone: "warning",
      }))
    ) {
      return;
    }

    const { error } = await api.feeSchedules.update(schedule.id, { active });
    if (error) return notify.error(error);

    notify.success(active ? "Schedule marked active" : "Schedule marked inactive");
    onChanged();
    invalidateTargets(qc);
  };

  return (
    <div>
      <PageHeader
        title={schedule.name}
        description={schedule.notes ?? ""}
        breadcrumbs={[{ label: "Fee Schedules", to: "/fee-schedules" }, { label: schedule.name }]}
        actions={
          <>
            {canEdit && (
              <>
                {detailsDirty && (
                  <Button variant="ghost" onClick={resetDetails} disabled={savingDetails}>
                    Discard
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={saveDetails}
                  disabled={savingDetails || !detailsDirty}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {savingDetails ? "Saving…" : detailsDirty ? "Save changes" : "Saved"}
                </Button>
                <Button onClick={() => setAddingVersion(true)}>
                  <CalendarPlus className="mr-2 h-4 w-4" /> New dated prices
                </Button>
              </>
            )}
            {/*
             * Readable by anyone who can read the page, not gated on `canEdit`:
             * an export writes nothing, and the price list is the thing a
             * read-only user most often opens a schedule to get hold of.
             */}
            <DataExportMenu groups={exportGroups} />
            <Button variant="outline" onClick={() => navigate({ to: "/fee-schedules" })}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to schedules
            </Button>
          </>
        }
      />

      {canEdit && (
        <div className="mb-4 rounded-md border bg-card p-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="schedule-name" className="text-xs text-muted-foreground" required>
                Name
              </Label>
              <Input
                id="schedule-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Group rates"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Client groups</Label>
              <MultiGroupPicker
                options={allGroups}
                value={groupIds}
                scheduleId={schedule.id}
                onChange={setGroupIds}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="schedule-notes" className="text-xs text-muted-foreground">
                Notes
              </Label>
              <Textarea
                id="schedule-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What this schedule is for, who agreed it, anything worth knowing next year."
              />
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact
          label="Dated versions"
          value={versions.length ? String(versions.length) : "none yet"}
        />
        <Fact label="Billing today" value={billingNow?.effective_from ?? "nothing"} />
        <div className="rounded-md border bg-card p-3">
          <div className="text-xs text-muted-foreground">Status</div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <StatusBadge schedule={schedule} />
            {canEdit && <Switch checked={schedule.active} onCheckedChange={toggleActive} />}
          </div>
        </div>
        {/*
         * The flag the service catalogue reads. On its own tile beside the status
         * because it is the other thing about this schedule that is true of the whole
         * set rather than of this row — and the only one of the two that changes what
         * another page shows.
         */}
        <div className="rounded-md border bg-card p-3">
          <div className="text-xs text-muted-foreground">Standard price list</div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {schedule.is_standard ? "Yes — the standard fees" : "No"}
            </span>
            {canEdit && (
              <Switch checked={!!schedule.is_standard} onCheckedChange={toggleStandard} />
            )}
          </div>
        </div>
      </div>

      {/* 
      <Alert className="mb-4">
        <AlertTitle>Who reads this schedule is set on the company, not here</AlertTitle>
        <AlertDescription>
          A company or client group points at a schedule from its own record — on the company's
          services page, on the group, or in bulk from a spreadsheet. A company reads its own
          schedule first, then its group's, then the Somasi standard fee for anything
          neither prices.
        </AlertDescription>
      </Alert> */}

      {latest && !latest.is_historical && (
        <Alert className="mb-4">
          <AlertTitle>A price change is queued for {latest.effective_from}</AlertTitle>
          <AlertDescription>
            Invoices dated before then still bill at the{" "}
            {billingNow ? `${billingNow.effective_from} prices` : "level below this schedule"}. The
            new prices take over on their own, with nothing further to switch.
          </AlertDescription>
        </Alert>
      )}

      {versions.length === 0 && !isLoading && (
        <Alert className="mb-4">
          <AlertTitle>No prices on this schedule yet</AlertTitle>
          <AlertDescription>
            <p>
              Prices are dated: each set applies from a day you choose until another set supersedes
              it, so an invoice for a past period always bills that period's rates. Add the first
              set to start pricing. Until then, every service falls back to the level below this
              schedule.
            </p>
            {canEdit && (
              <Button size="sm" className="mt-3" onClick={() => setAddingVersion(true)}>
                <CalendarPlus className="mr-2 h-4 w-4" /> Add the first prices
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">Effective from</TableHead>
              <TableHead className="w-36">Until</TableHead>
              <TableHead className="w-32">State</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="w-px whitespace-nowrap text-right">
                {canEdit && "Actions"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  Loading…
                </TableCell>
              </TableRow>
            ) : shown.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No dated prices on this schedule.
                </TableCell>
              </TableRow>
            ) : (
              shown.map((v) => {
                const state = versionState(v, billingNow?.effective_from ?? null);
                return (
                  <TableRow
                    key={v.effective_from}
                    className="cursor-pointer"
                    onClick={() => openVersion(v.effective_from)}
                  >
                    <TableCell className="font-medium tabular-nums">
                      {/* Underlined because the whole row opens this version. */}
                      <span className="underline underline-offset-2">{v.effective_from}</span>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {v.effective_to ?? "open"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          state.tone === "live"
                            ? "default"
                            : state.tone === "future"
                              ? "secondary"
                              : "outline"
                        }
                      >
                        {state.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs text-sm text-muted-foreground">
                      <Truncated text={v.notes} />
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <RowActions>
                          <RowAction
                            label="Delete"
                            destructive
                            onClick={(e) => {
                              e.stopPropagation();
                              void removeVersion(v);
                            }}
                          >
                            <Trash2 />
                          </RowAction>
                        </RowActions>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        pagination={pager}
        total={versions.length}
        loading={isLoading}
        unit="version"
      />

      <VersionDialog
        open={addingVersion}
        schedule={schedule}
        versions={versions}
        onClose={() => setAddingVersion(false)}
        onCreated={openVersion}
      />
    </div>
  );
}

/**
 * Which client groups read this schedule.
 *
 * Several, because a schedule may price more than one group on the same rates. A
 * group already reading another schedule stays pickable and is flagged rather than
 * hidden: picking it moves it, which is a real thing to want and an expensive thing
 * to do by accident, so it is shown rather than silently prevented.
 */
function MultiGroupPicker({
  options,
  value,
  scheduleId,
  onChange,
}: {
  options: { id: string; name: string; fee_schedule_id: string | null }[];
  value: string[];
  /** This schedule, so "already attached elsewhere" excludes itself. */
  scheduleId: string;
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  const chosen = options.filter((o) => value.includes(o.id));

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="h-auto min-h-9 w-full justify-between font-normal"
        >
          <span className="flex flex-wrap gap-1 py-0.5 text-left">
            {chosen.length === 0 ? (
              <span className="text-muted-foreground">No client groups</span>
            ) : (
              chosen.map((o) => (
                <Badge key={o.id} variant="secondary" className="font-normal">
                  {o.name}
                </Badge>
              ))
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search client groups…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty className="p-3 text-sm text-muted-foreground">
              Nothing matches.
            </CommandEmpty>
            <CommandGroup>
              {matches.map((o) => {
                const on = value.includes(o.id);
                const elsewhere = !on && !!o.fee_schedule_id && o.fee_schedule_id !== scheduleId;
                return (
                  <CommandItem key={o.id} value={o.id} onSelect={() => toggle(o.id)}>
                    <Check className={cn("mr-2 h-4 w-4", on ? "opacity-100" : "opacity-0")} />
                    <span className="flex-1">{o.name}</span>
                    {elsewhere && (
                      <Badge variant="outline" className="ml-2 font-normal">
                        on another schedule
                      </Badge>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}
